import { ReactElement, useEffect } from 'react'
import { Alert, Box, Button, Card, Typography } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CourseOverviewSkeleton } from 'src/v3/classroom-v2/components/overview-skeleton'
import { SurveyIntro, unansweredSurveyFields } from 'src/v3/components/survey'
import { SurveyForm } from 'src/v3/classroom-v2/components/survey-form'
import {
  selectSurveyAttemptByCourseID,
  surveyAnswerChanged,
  surveyOpened,
  surveySubmitAttempted,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { CourseV2SurveyPrerequisite } from 'src/v3/classroom-v2/types'

// What the learner still owes, in their words rather than the server's. The
// vocabulary is shared with the 409's unmetPrerequisites deliberately, so both
// the locked-before-you-start case and the refused-on-submit case say the same
// thing about the same missing work.
let prerequisiteLabel = (prerequisite: CourseV2SurveyPrerequisite): string =>
  prerequisite === 'courseStepsIncomplete'
    ? 'Finish every course step.'
    : 'Pass the course assessment.'

// Wires the shared survey fields to the wizard slice. Same division of labour as
// QuizStep: the answers, the in-flight flag and the verdict all live in the
// slice's CourseV2SurveyAttempt union, and this holds no useState — so a reload
// or a stray re-render can't leave the form showing something the store doesn't
// hold, and nothing here can advance the user out of the survey phase. Only the
// server's refetched progress record does that.
export let SurveyStep = ({ courseID }: { courseID: string }): ReactElement => {
  let dispatch = useAppDispatch()
  let attempt = useAppSelector(selectSurveyAttemptByCourseID(courseID))

  // Being in the survey phase IS the cue to open a draft — the phase came from
  // the server saying the survey is outstanding, so there's no user action to
  // wait for. surveyOpened is idempotent, and a null attempt is the only
  // trigger, so this can't loop or wipe answers on a re-render.
  useEffect(() => {
    if (attempt === null) dispatch(surveyOpened(courseID))
  }, [attempt === null, courseID])

  if (attempt === null) return <CourseOverviewSkeleton />

  // The server refused because work is still owed. Rendered as what's left to do
  // rather than as an error: nothing failed, the user is simply early. No retry
  // button — retrying would post the same body to the same refusal. They leave
  // by finishing the listed work, which moves the progress record and with it
  // the phase.
  if (attempt.kind === 'prerequisitesNotMet') {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Card variant="outlined" sx={{ p: 4 }}>
          <Typography variant="h1" sx={{ fontSize: '1.4rem', mb: 1 }}>
            Not quite ready for the survey
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete the following, and the survey will open automatically:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {attempt.unmetPrerequisites.map((prerequisite) => (
              <Typography
                component="li"
                variant="body2"
                key={prerequisite}
                sx={{ mb: 0.5 }}
              >
                {prerequisiteLabel(prerequisite)}
              </Typography>
            ))}
          </Box>
        </Card>
      </Box>
    )
  }

  // Both terminal-success states. 'settled' is the two non-error 409s (already
  // submitted, never required) — the user is past this step either way, and the
  // middleware's progress refetch is already in flight to move the phase on, so
  // this is a brief acknowledgement rather than a destination.
  if (attempt.kind === 'submitted' || attempt.kind === 'settled') {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Card variant="outlined" sx={{ p: 4 }}>
          <Typography variant="h1" sx={{ fontSize: '1.4rem', mb: 1 }}>
            Thank you for your feedback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your survey is on record. Finishing up your course now…
          </Typography>
        </Card>
      </Box>
    )
  }

  let isSubmitting = attempt.kind === 'submitting'
  // Only surfaced after a submit press. Every question is required, so a fresh
  // form is entirely incomplete and flagging all thirteen on arrival would be
  // scolding the learner for not having started.
  let unanswered =
    attempt.kind === 'editing' && attempt.hasAttemptedSubmit
      ? unansweredSurveyFields(attempt.draft)
      : []

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto' }}>
      <Typography variant="h1" className="sr-only">
        Course Survey
      </Typography>
      {/* Not a <form>: there is no browser submit to intercept, and the button
          below announces the press rather than posting anything itself. */}
      <SurveyIntro />
      {attempt.kind === 'editing' && attempt.submitError && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {attempt.submitError}
        </Alert>
      )}
      {unanswered.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          Please answer the remaining{' '}
          {unanswered.length === 1
            ? 'question'
            : `${unanswered.length} questions`}{' '}
          before submitting. They&apos;re marked below.
        </Alert>
      )}
      <Typography color="text.secondary" variant="body2" sx={{ mb: 3, mx: 2 }}>
        All questions are required.
      </Typography>
      <SurveyForm
        values={attempt.draft}
        isDisabled={isSubmitting}
        unansweredFields={unanswered}
        onChangeLikert={(name, value) =>
          dispatch(surveyAnswerChanged({ courseID, name, value }))
        }
        onChangeFreeText={(name, value) =>
          dispatch(surveyAnswerChanged({ courseID, name, value }))
        }
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        {/* Always enabled while editing. A disabled button would leave a learner
            with no way to find out WHICH question they missed; pressing it is
            what surfaces that, and the middleware decides whether the press
            becomes a POST. */}
        <Button
          variant="contained"
          size="large"
          disabled={isSubmitting}
          aria-label={
            isSubmitting ? 'Submitting survey, please wait' : 'Submit survey'
          }
          onClick={() => dispatch(surveySubmitAttempted(courseID))}
        >
          {isSubmitting ? 'Submitting…' : 'Submit Survey'}
        </Button>
      </Box>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
        }}
      >
        {isSubmitting ? 'Survey is being submitted' : ''}
      </Box>
    </Box>
  )
}
